import type {ReactElement, Ref} from 'react';
import React, {forwardRef} from 'react';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {NavRightSideOptionTypes} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options/nav-right-options.types';
import styles from './base-nav-option.module.scss';

interface BaseNavOptionProps {
  id: string;
  className?: string;
  btnClasses?: string;
  icon: string;
  text: string;
  iconType?: IconType;
  buttonType?: Type;
  optionType: NavRightSideOptionTypes;
  hasMarginRight?: boolean;
  disabled?: boolean;
  onClickHandler(): void;
  isLoading?: boolean;
  iconPlacement?: IconPlacement;
  isActive?: boolean;
}

const BaseNavOption = forwardRef(
  (
    {
      className = '',
      btnClasses = '',
      disabled = false,
      iconType = IconType.DARK_GHOST,
      buttonType = Type.DARK_GHOST,
      hasMarginRight = false,
      isLoading = false,
      iconPlacement = IconPlacement.LEFT,
      isActive = false,
      ...props
    }: BaseNavOptionProps,
    ref: Ref<HTMLElement>
  ): ReactElement | null => {
    const getContentFromType = (): ReactElement | null => {
      switch (props.optionType) {
        case NavRightSideOptionTypes.BUTTON:
          return (
            <Button
              ref={ref as React.Ref<HTMLButtonElement>}
              key={props.id}
              onClick={(): void => {
                props.onClickHandler();
              }}
              customClasses={`${hasMarginRight ? 'spacing-m-r-2' : ''} ${className} ${btnClasses}`}
              size={Size.SMALL}
              type={buttonType}
              text={props.text}
              icon={props.icon}
              disabled={disabled}
              isLoading={isLoading}
              iconPlacement={iconPlacement}
              isActive={isActive}
            />
          );
        case NavRightSideOptionTypes.ICON:
          if (isLoading) {
            // only supported for dark ghost square size 20
            return (
              <div ref={ref as React.Ref<HTMLDivElement>} className={`${styles.loader} hover-transition-all flex-center ${hasMarginRight ? 'spacing-m-r-2' : ''} radius-4`}>
                <CircularProgressLoader classes="spacing-m-0" color={LOADER_COLOR.WHITE} size={LOADER_SIZE.SMALL} />
              </div>
            );
          }
          return (
            <Icon
              ref={ref as React.Ref<HTMLDivElement>}
              key={props.id}
              onClick={(): void => {
                props.onClickHandler();
              }}
              className={`${hasMarginRight ? 'spacing-m-r-2' : ''} ${className}`}
              icon={props.icon}
              size={IconSize.SIZE_ICON_20}
              type={iconType}
              shape={IconShape.SQUARE}
              isDisabled={disabled}
              isLoading={isLoading}
            />
          );
        case NavRightSideOptionTypes.HIDDEN:
          return null;
        default:
          throw new Error('Unhandled Navbar right side option type');
      }
    };

    return getContentFromType();
  }
);

export default BaseNavOption;
